/**
 * Live HTTP implementation of the appointments feature API (P1b FE wire).
 *
 * Targets the Phase P1b backend surface at `/api/biz/appointments/*`
 * (see `backend/src/api/business/biz-booking-routes.ts`). All requests
 * carry the active branch via the `x-business-branch-id` header injected
 * by `bizClient`; query-string `branchId` is also passed for the routes
 * that take a query param so the BE handlers can short-circuit before
 * hitting RLS.
 *
 * Contract alignment strategy:
 *   The backend `BookingRow` is a near-superset of the FE
 *   `AppointmentRecord`:
 *     - extra fields: `serviceId`, `priceMinor`, nullable `petId/petName/staffId`
 *     - missing fields: none
 *   `bookingRowToRecord()` projects BookingRow → AppointmentRecord:
 *     - `priceMinor` (BIGINT minor units) → `price` (major TRY)
 *     - nullable petId/petName/staffId → '' (FE rendering expects strings)
 *     - preserves Connect source/link metadata for marketplace-origin display
 *
 *   `ServiceProjection.priceMinor` is also dropped; only `price` (major)
 *   is kept to match `ServiceRecord`.
 *
 *   `CustomerProjection` exposes `loyaltyPoints/totalSpend/visitCount` as
 *   numbers, but `CustomerRecord` keeps them optional. We pass them
 *   through verbatim — the FE adapter tolerates both shapes.
 *
 * Branch resolution:
 *   `getActiveBranchId()` reads the `petota-business:active-branch-id`
 *   localStorage key set during login. Mutations and list endpoints
 *   alike require an active branch — without it we throw an `ApiError`
 *   with a typed Problem-Details payload so the React Query layer can
 *   surface a friendly toast instead of leaking a generic 422.
 *
 * Out-of-scope deferrals (intentional):
 *   - serviceId mutation through `editAppointment` (FE
 *     EditAppointmentPayload shape lacks `serviceId`; service swap will
 *     ship together with B1.5 AppointmentForm v2).
 *   - customerName/petName/serviceName drift fields are dropped on
 *     update — the BE re-derives them from FK joins on the next list
 *     refresh, and React Query invalidates the week query after every
 *     mutation, so the cache converges.
 *
 * @see appointments-api-mock.ts (fixture path)
 * @see appointments-api.ts (mock↔live dispatcher)
 * @see backend/src/api/business/biz-booking-routes.ts
 * @see backend/src/domains/business/biz-booking-service.ts
 */
import { ApiError, getActiveBranchId } from '@/shared/api/apiClient';
import { bizGet, bizPatch, bizPost } from '@/shared/api/bizClient';

import type {
  AppointmentRecord,
  CancelAppointmentPayload,
  CreateCashierOrderFromAppointmentPayload,
  CreateCashierOrderFromAppointmentResult,
  CreateAppointmentPayload,
  CustomerRecord,
  EditAppointmentPayload,
  PetRecord,
  ServiceRecord,
  StaffRecord,
  UpdateAppointmentPayload,
  WeekRangeQuery,
} from './types';

// ---------------------------------------------------------------------------
// Backend response shapes (mirror biz-booking-service / biz-reference-service)
// ---------------------------------------------------------------------------

interface BookingRow {
  readonly id: string;
  readonly source: AppointmentRecord['source'];
  readonly externalBookingId: string | null;
  readonly customerId: string;
  readonly customerName: string;
  readonly petId: string | null;
  readonly petName: string | null;
  readonly serviceId: string;
  readonly serviceName: string;
  readonly serviceCategory: AppointmentRecord['serviceCategory'];
  readonly staffId: string | null;
  readonly status: AppointmentRecord['status'];
  readonly startTime: string;
  readonly endTime: string;
  readonly isVip: boolean;
  readonly notes: string | null;
  readonly priceMinor: number | null;
  readonly currency: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

interface StaffProjection {
  readonly id: string;
  readonly fullName: string;
  readonly avatarUrl: string | null;
  readonly role: string;
  readonly isActive: boolean;
  readonly createdAt: string;
}

interface CustomerProjection {
  readonly id: string;
  readonly fullName: string;
  readonly phone: string;
  readonly email: string | null;
  readonly avatarUrl: string | null;
  readonly isVip: boolean;
  readonly loyaltyTier: CustomerRecord['loyaltyTier'];
  readonly loyaltyPoints: number;
  readonly totalSpend: number;
  readonly visitCount: number;
  readonly firstVisitAt: string | null;
  readonly lastVisitAt: string | null;
  readonly tags: string[];
  readonly internalNotes: string | null;
  readonly preferredStaffId: string | null;
  readonly preferredContactChannel: CustomerRecord['preferredContactChannel'];
  readonly createdAt: string;
}

interface PetProjection {
  readonly id: string;
  readonly customerId: string;
  readonly name: string;
  readonly species: PetRecord['species'];
  readonly breed: string | null;
  readonly photoUrl: string | null;
  readonly birthDate: string | null;
  readonly sex: PetRecord['sex'] | null;
  readonly neutered: boolean | null;
  readonly weightKg: number | null;
  readonly allergies: string[];
  readonly chronicConditions: string[];
  readonly behaviorNotes: string | null;
  readonly microchipId: string | null;
  readonly createdAt: string;
}

interface ServiceProjection {
  readonly id: string;
  readonly externalOfferingId: string | null;
  readonly name: string;
  readonly category: ServiceRecord['category'];
  readonly durationMin: number;
  readonly price: number;
  readonly priceMinor: number;
  readonly currency: string;
  readonly qualifiedStaffIds: string[];
  readonly createdAt: string;
}

interface ListEnvelope<T> {
  readonly items: readonly T[];
}

// ---------------------------------------------------------------------------
// Branch resolution
// ---------------------------------------------------------------------------

function requireBranchId(): string {
  const branchId = getActiveBranchId();
  if (!branchId) {
    throw new ApiError(
      {
        type: 'petota:biz-appointments/branch-not-resolved',
        title: 'Active branch not selected',
        status: 409,
        detail:
          'Appointment endpoints require an active branch. Select a branch before loading the calendar.',
      },
      409,
    );
  }
  return branchId;
}

// ---------------------------------------------------------------------------
// Adapters (BE row → FE record)
// ---------------------------------------------------------------------------

function bookingRowToRecord(row: BookingRow): AppointmentRecord {
  const record: AppointmentRecord = {
    id: row.id,
    source: row.source,
    externalBookingId: row.externalBookingId,
    customerId: row.customerId,
    customerName: row.customerName,
    petId: row.petId ?? '',
    petName: row.petName ?? '',
    serviceName: row.serviceName,
    serviceCategory: row.serviceCategory,
    staffId: row.staffId ?? '',
    status: row.status,
    startTime: row.startTime,
    endTime: row.endTime,
    isVip: row.isVip,
    createdAt: row.createdAt,
    updatedAt: row.updatedAt,
    currency: row.currency,
  };
  if (row.notes !== null && row.notes !== undefined) {
    record.notes = row.notes;
  }
  if (row.priceMinor !== null && row.priceMinor !== undefined) {
    record.price = row.priceMinor / 100;
  }
  return record;
}

function staffProjectionToRecord(p: StaffProjection): StaffRecord {
  const staff: StaffRecord = {
    id: p.id,
    fullName: p.fullName,
    role: p.role,
    isActive: p.isActive,
    createdAt: p.createdAt,
  };
  if (p.avatarUrl !== null && p.avatarUrl !== undefined) {
    staff.avatarUrl = p.avatarUrl;
  }
  return staff;
}

function customerProjectionToRecord(p: CustomerProjection): CustomerRecord {
  const record: CustomerRecord = {
    id: p.id,
    fullName: p.fullName,
    phone: p.phone,
    isVip: p.isVip,
    createdAt: p.createdAt,
  };
  if (p.email !== null) record.email = p.email;
  if (p.avatarUrl !== null) record.avatarUrl = p.avatarUrl;
  if (p.loyaltyTier !== null) record.loyaltyTier = p.loyaltyTier;
  if (p.loyaltyPoints !== undefined) record.loyaltyPoints = p.loyaltyPoints;
  if (p.totalSpend !== undefined) record.totalSpend = p.totalSpend;
  if (p.visitCount !== undefined) record.visitCount = p.visitCount;
  if (p.firstVisitAt !== null) record.firstVisitAt = p.firstVisitAt;
  if (p.lastVisitAt !== null) record.lastVisitAt = p.lastVisitAt;
  if (p.tags.length > 0) record.tags = [...p.tags];
  if (p.internalNotes !== null) record.internalNotes = p.internalNotes;
  if (p.preferredStaffId !== null) record.preferredStaffId = p.preferredStaffId;
  if (p.preferredContactChannel !== null) {
    record.preferredContactChannel = p.preferredContactChannel;
  }
  return record;
}

function petProjectionToRecord(p: PetProjection): PetRecord {
  const record: PetRecord = {
    id: p.id,
    customerId: p.customerId,
    name: p.name,
    species: p.species,
    createdAt: p.createdAt,
  };
  if (p.breed !== null) record.breed = p.breed;
  if (p.photoUrl !== null) record.photoUrl = p.photoUrl;
  if (p.birthDate !== null) record.birthDate = p.birthDate;
  if (p.sex !== null) record.sex = p.sex;
  if (p.neutered !== null) record.neutered = p.neutered;
  if (p.weightKg !== null) record.weightKg = p.weightKg;
  if (p.allergies.length > 0) record.allergies = [...p.allergies];
  if (p.chronicConditions.length > 0) {
    record.chronicConditions = [...p.chronicConditions];
  }
  if (p.behaviorNotes !== null) record.behaviorNotes = p.behaviorNotes;
  if (p.microchipId !== null) record.microchipId = p.microchipId;
  return record;
}

function serviceProjectionToRecord(p: ServiceProjection): ServiceRecord {
  const record: ServiceRecord = {
    id: p.id,
    externalOfferingId: p.externalOfferingId,
    name: p.name,
    category: p.category,
    durationMin: p.durationMin,
    price: p.price,
    currency: p.currency,
    createdAt: p.createdAt,
  };
  if (p.qualifiedStaffIds.length > 0) {
    record.qualifiedStaffIds = [...p.qualifiedStaffIds];
  }
  return record;
}

// ---------------------------------------------------------------------------
// Mutation body translators
// ---------------------------------------------------------------------------

interface CreateBookingBody {
  branchId: string;
  customerId: string;
  petId: string | null;
  serviceId: string;
  staffId: string | null;
  startsAt: string;
  endsAt: string;
  status?: AppointmentRecord['status'];
  isVip?: boolean;
  notes?: string | null;
  priceMinor?: number | null;
  currency?: string;
  source?: AppointmentRecord['source'];
  externalBookingId?: string | null;
}

interface UpdateBookingBody {
  customerId?: string;
  petId?: string | null;
  serviceId?: string;
  staffId?: string | null;
  startsAt?: string;
  endsAt?: string;
  status?: AppointmentRecord['status'];
  isVip?: boolean;
  notes?: string | null;
  priceMinor?: number | null;
  currency?: string;
}

function buildCreateBody(
  payload: CreateAppointmentPayload,
  branchId: string,
): CreateBookingBody {
  const body: CreateBookingBody = {
    branchId,
    customerId: payload.customerId,
    petId: payload.petId || null,
    serviceId: payload.serviceId,
    staffId: payload.staffId || null,
    startsAt: payload.startTime,
    endsAt: payload.endTime,
    source: 'staff_manual',
  };
  if (payload.status !== undefined) body.status = payload.status;
  if (payload.isVip !== undefined) body.isVip = payload.isVip;
  if (payload.notes !== undefined) body.notes = payload.notes;
  if (payload.currency !== undefined) body.currency = payload.currency;
  if (payload.price !== undefined && payload.price !== null) {
    body.priceMinor = Math.round(payload.price * 100);
  }
  return body;
}

/**
 * Translate the FE `UpdateAppointmentPayload.changes` (or the supported subset
 * of `EditAppointmentPayload.changes`) into the BE `UpdateBookingBody` shape.
 *
 * Drift fields that the BE re-derives (`customerName`, `petName`, `serviceName`,
 * `serviceCategory`) are intentionally dropped — the BookingRow returned by
 * the BE re-supplies them.
 */
function buildUpdateBody(
  changes: UpdateAppointmentPayload['changes'] | EditAppointmentPayload['changes'],
): UpdateBookingBody {
  const body: UpdateBookingBody = {};
  if ('customerId' in changes && changes.customerId !== undefined) {
    body.customerId = changes.customerId;
  }
  if ('petId' in changes && changes.petId !== undefined) {
    body.petId = changes.petId || null;
  }
  if ('staffId' in changes && changes.staffId !== undefined) {
    body.staffId = changes.staffId || null;
  }
  if ('startTime' in changes && changes.startTime !== undefined) {
    body.startsAt = changes.startTime;
  }
  if ('endTime' in changes && changes.endTime !== undefined) {
    body.endsAt = changes.endTime;
  }
  if ('status' in changes && changes.status !== undefined) {
    body.status = changes.status;
  }
  if ('isVip' in changes && changes.isVip !== undefined) {
    body.isVip = changes.isVip;
  }
  if ('notes' in changes && changes.notes !== undefined) {
    body.notes = changes.notes ?? null;
  }
  if ('currency' in changes && changes.currency !== undefined) {
    body.currency = changes.currency;
  }
  if ('price' in changes && changes.price !== undefined) {
    body.priceMinor = changes.price === null ? null : Math.round(changes.price * 100);
  }
  return body;
}

// ---------------------------------------------------------------------------
// Public API — matches `appointments-api-mock.ts` signatures exactly
// ---------------------------------------------------------------------------

export async function listStaff(): Promise<StaffRecord[]> {
  const branchId = requireBranchId();
  const res = await bizGet<ListEnvelope<StaffProjection>>(
    '/biz/appointments/staff',
    { params: { branchId } },
  );
  return res.items.map(staffProjectionToRecord);
}

export async function listAppointments(
  query: WeekRangeQuery,
): Promise<AppointmentRecord[]> {
  const branchId = requireBranchId();
  const start = new Date(query.weekStart);
  if (Number.isNaN(start.getTime())) {
    throw new ApiError(
      {
        type: 'petota:biz-appointments/invalid-week-start',
        title: 'Invalid week start',
        status: 422,
        detail: `weekStart "${query.weekStart}" is not a valid ISO-8601 timestamp.`,
      },
      422,
    );
  }
  const end = new Date(start.getTime() + 7 * 24 * 60 * 60 * 1000);
  const res = await bizGet<ListEnvelope<BookingRow>>('/biz/appointments', {
    params: {
      branchId,
      from: start.toISOString(),
      to: end.toISOString(),
    },
  });
  return res.items.map(bookingRowToRecord);
}

export async function updateAppointment(
  payload: UpdateAppointmentPayload,
): Promise<AppointmentRecord> {
  const body = buildUpdateBody(payload.changes);
  const row = await bizPatch<BookingRow, UpdateBookingBody>(
    `/biz/appointments/${payload.id}`,
    body,
  );
  return bookingRowToRecord(row);
}

export async function cancelAppointment(
  payload: CancelAppointmentPayload,
): Promise<AppointmentRecord> {
  const row = await bizPost<BookingRow, { reason: string }>(
    `/biz/appointments/${payload.id}/cancel`,
    { reason: payload.reason },
  );
  return bookingRowToRecord(row);
}

export async function createCashierOrderFromAppointment(
  payload: CreateCashierOrderFromAppointmentPayload,
): Promise<CreateCashierOrderFromAppointmentResult> {
  return bizPost<CreateCashierOrderFromAppointmentResult>(
    `/biz/appointments/${payload.id}/cashier-order`,
    {},
  );
}

export async function listCustomers(): Promise<CustomerRecord[]> {
  const res = await bizGet<ListEnvelope<CustomerProjection>>(
    '/biz/appointments/customers',
  );
  return res.items.map(customerProjectionToRecord);
}

export async function listPets(customerId?: string): Promise<PetRecord[]> {
  const params = customerId ? { customerId } : undefined;
  const res = await bizGet<ListEnvelope<PetProjection>>(
    '/biz/appointments/pets',
    params ? { params } : undefined,
  );
  return res.items.map(petProjectionToRecord);
}

export async function listServices(): Promise<ServiceRecord[]> {
  const branchId = requireBranchId();
  const res = await bizGet<ListEnvelope<ServiceProjection>>(
    '/biz/appointments/services',
    { params: { branchId } },
  );
  return res.items.map(serviceProjectionToRecord);
}

export async function createAppointment(
  payload: CreateAppointmentPayload,
): Promise<AppointmentRecord> {
  const branchId = requireBranchId();
  const body = buildCreateBody(payload, branchId);
  const row = await bizPost<BookingRow, CreateBookingBody>(
    '/biz/appointments',
    body,
  );
  return bookingRowToRecord(row);
}

export async function editAppointment(
  payload: EditAppointmentPayload,
): Promise<AppointmentRecord> {
  const body = buildUpdateBody(payload.changes);
  const row = await bizPatch<BookingRow, UpdateBookingBody>(
    `/biz/appointments/${payload.id}`,
    body,
  );
  return bookingRowToRecord(row);
}

/**
 * No-op in live mode — the localStorage fixture only matters when
 * `APP_ENV.bizApiMode === 'mock'`. Kept to preserve the dispatcher
 * surface so consumers don't need to gate on the mode flag.
 */
export function resetAppointmentMock(): void {
  // intentional no-op (live mode has no fixture to reset)
}
